import { Fragment } from 'react';
import {
  ActionSchema,
  ChildListSchema,
  DynamicStringSchema,
  DynamicValueSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import {
  QuoteOption,
  type QuoteGenerateResult,
  type QuoteGenerateError,
} from './QuoteOption';

interface ResolvedProps {
  readonly label?: string;
  readonly limit?: string;
  readonly retention?: string;
  readonly price?: string;
  readonly diffs?: string;
  readonly detailedDiffs?: string;
  readonly coverages?: string;
  readonly status?: string;
  readonly errorReason?: string;
  readonly statusLabel?: string;
  // Populated reactively by A2UISurfaceList's result-injection branch at
  // `/results/<this component's id>`, never sent by the agent up front —
  // either a real QuoteGenerateResult (success) or a QuoteGenerateError
  // (failure), written to the same path.
  readonly result?: QuoteGenerateResult | QuoteGenerateError;
  readonly onGenerate?: () => void;
  readonly onOpenReview?: () => void;
  /** Child component ids (e.g. an attached QuoteNote), in display order. */
  readonly children?: readonly string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  label: DynamicStringSchema,
  limit: DynamicStringSchema,
  retention: DynamicStringSchema,
  price: DynamicStringSchema,
  diffs: DynamicStringSchema,
  detailedDiffs: DynamicStringSchema,
  coverages: DynamicStringSchema,
  status: DynamicStringSchema,
  errorReason: DynamicStringSchema,
  statusLabel: DynamicStringSchema,
  result: DynamicValueSchema,
  onGenerate: ActionSchema,
  onOpenReview: ActionSchema,
  children: ChildListSchema,
}) as any;

export const QuoteOptionImpl = createComponentImplementation(
  { name: 'QuoteOption', schema },
  ({
    props,
    buildChild,
  }: {
    props: ResolvedProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildChild: (childId: string) => any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
  }) => (
    <QuoteOption
      label={props.label ?? ''}
      limit={props.limit ?? ''}
      retention={props.retention ?? ''}
      price={props.price ?? ''}
      diffs={props.diffs}
      detailedDiffs={props.detailedDiffs}
      coverages={props.coverages}
      status={props.status === 'ERROR' ? 'ERROR' : 'OK'}
      errorReason={props.errorReason}
      statusLabel={props.statusLabel}
      result={
        props.result as QuoteGenerateResult | QuoteGenerateError | undefined
      }
      onGenerate={props.onGenerate}
      onOpenReview={props.onOpenReview}
    >
      {(Array.isArray(props.children) ? props.children : []).map((childId) => (
        <Fragment key={childId}>{buildChild(childId)}</Fragment>
      ))}
    </QuoteOption>
  )
);
